import {
  Chip,
  Flex,
  GoodsCarousel as UIKitGoodsCarousel,
  GoodsCarouselSkeleton,
  List,
  RemoteImage,
  Typography,
} from '@banhala/ui-kit';
import type { GoodsCarouselProps } from './types';

import { bridge, isNotNil, useNavigator } from '../../../../utils';

import { logOnGoodsPressItem } from './log/logOnPressItem';
import type { GoodsCoreModel, IScreensLogging } from '../../../../api/types';
import { logOnPressLike } from './log/logOnPressLike';
import { useAddLikeGoods } from '../../../../hooks';
import { useLogDisplayGoods } from './log/useLogDisplayGoods';
import { useLogDisplayCarousel } from './log/useLogDisplayCarousel';
import { getGoodsCarouselData } from './goodsCarouselDataConverter';
import { useEffect, useMemo } from 'react';
import { useRemoveLikeGoods } from '../../../../queries';

const VIEWABILITY_CONFIG = { itemVisiblePercentThreshold: 50 };

/**
 *
 * @example
 * <AtomicRenderer data={atomicRendererDataConverter(mockHeadlineSmallOneLineData)} />
 * // mockHeadlineSmallOneLineData를 사용하면 GoodsCarousel까지 확인할 수 있습니다.
 */
export const GoodsCarousel = ({
  entity,
  likeMap,
  setLikeMap,
  screenName,
  isFetching = false,
  atomicParams = {},
  onChipPress,
  initParams,
  ...restProps
}: GoodsCarouselProps) => {
  const { navigator } = useNavigator();
  const radio = useMemo(
    () => restProps.body?.item.radio ?? null,
    [restProps.body],
  );
  const subRadio = useMemo(
    () => restProps.body?.item.sub_radio ?? null,
    [restProps.body],
  );

  useEffect(() => {
    initParams?.({
      ...(radio ? { [radio.query_name]: radio.selected_value } : {}),
      ...(subRadio ? { [subRadio.query_name]: subRadio.selected_value } : {}),
    });
  }, [radio, subRadio, initParams]);

  const goodsData = getGoodsCarouselData({ entity });

  useEffect(() => {
    setLikeMap(prevState => {
      return {
        ...prevState,
        ...entity?.reduce<Record<string, boolean>>((acc, cur) => {
          acc[cur.item.sno] =
            prevState[cur.item.sno] ?? cur.item.like?.is_liked ?? false;
          return acc;
        }, {}),
      };
    });
  }, [entity, setLikeMap]);
  const { mutateAsync: mutateRemoveLikeGoods } = useRemoveLikeGoods();
  const { logDisplayGoods } = useLogDisplayGoods();
  useLogDisplayCarousel(restProps.logging, restProps.isVisible);

  const { addLikeGoods } = useAddLikeGoods({
    listParams: entity?.[0]?.logging.analytics?.LIST_ID as
      | string
      | undefined,
    onChangeIsLiked: (sno, nextValue) => {
      setLikeMap(prevState => ({ ...prevState, [sno]: nextValue }));
    },
    screen:
      screenName ??
      (entity?.[0]?.logging.analytics?.SCREEN as string | undefined) ??
      '',
  });

  if (!entity) {
    return null;
  }

  const handlePressItem = async ({
    logging,
    item,
  }: {
    logging: IScreensLogging;
    item: GoodsCoreModel;
  }) => {
    logOnGoodsPressItem({ item, logging });
    navigator.moveToGoods({
      sno: item.sno,
      firstPageRendering: item.first_page_rendering ?? null,
      remoteDeeplink: item.remote_deeplink ?? undefined,
      loggingAnalytics: logging.analytics,
    });
  };

  const handlePressLike = async ({
    logging,
    item,
  }: {
    logging: IScreensLogging;
    item: GoodsCoreModel;
  }) => {
    const currentLike = likeMap[item.sno] ?? false;
    logOnPressLike({ logging, item, currentLike });
    if (currentLike) {
      await mutateRemoveLikeGoods({
        sno: item.sno,
        list_params_referrer: logging.analytics?.LIST_ID as string | undefined,
      });
      bridge.likeGoods({
        goods_sno: item.sno,
        is_liked: false,
      });
      setLikeMap(prevState => ({ ...prevState, [item.sno]: false }));
      return;
    }
    addLikeGoods(item.sno, logging.analytics, logging.ably_only_log);
  };

  return (
    <Flex>
      {isNotNil(radio) && radio.possible_items.length > 0 && (
        <Flex>
          <List
            horizontal={true}
            contentContainerStyle={{
              paddingTop: 8,
              paddingBottom: 12,
              paddingHorizontal: 12,
              gap: 8,
            }}
            data={radio.possible_items}
            extraData={atomicParams}
            showsHorizontalScrollIndicator={false}
            renderItem={({ item }) => {
              const isSelected =
                atomicParams[radio.query_name] === item.query_value;

              if (radio?.view_type === 'HEADLINE_TAB') {
                return (
                  <Flex.Row
                    gap={2}
                    alignItems={'center'}
                    borderBottomWidth={2}
                    borderColor={isSelected ? 'content_primary' : 'white'}
                    padding={{ horizontal: 4, top: 4, bottom: 10 }}
                    onPress={() =>
                      onChipPress?.(radio.query_name, item.query_value)
                    }
                  >
                    <Typography type={'h5'}>{item.text}</Typography>
                    {isNotNil(item.image) &&
                      isNotNil(item.image_width) &&
                      isNotNil(item.image_height) && (
                        <RemoteImage
                          uri={item.image}
                          width={item.image_width}
                          height={item.image_height}
                        />
                      )}
                  </Flex.Row>
                );
              }
              return (
                <Chip
                  state={isSelected ? 'active' : 'enabled'}
                  size={'medium'}
                  onPress={() =>
                    onChipPress?.(radio.query_name, item.query_value)
                  }
                >
                  {item.text}
                </Chip>
              );
            }}
          />
        </Flex>
      )}
      {isNotNil(subRadio) && subRadio.possible_items.length > 0 && (
        <List
          horizontal={true}
          contentContainerStyle={{
            paddingBottom: 12,
            paddingHorizontal: 12,
            gap: 8,
          }}
          data={subRadio.possible_items}
          extraData={atomicParams}
          showsHorizontalScrollIndicator={false}
          renderItem={({ item }) => {
            const isSelected =
              atomicParams[subRadio.query_name] === item.query_value;

            return (
              <Flex.Row
                justifyContent={'center'}
                alignItems={'center'}
                padding={{
                  vertical: 4,
                  left: isNotNil(item.image) ? 4 : 12,
                  right: 12,
                }}
                borderWidth={1}
                borderColor={isSelected ? 'content_primary' : 'border_tertiary'}
                borderRadius={32}
                gap={4}
                onPress={() =>
                  onChipPress?.(subRadio.query_name, item.query_value)
                }
              >
                {isNotNil(item.image) && (
                  <RemoteImage
                    uri={item.image}
                    width={28}
                    height={28}
                    borderRadius={'xlarge'}
                  />
                )}
                <Typography
                  type={'subtitle3'}
                  color={isSelected ? 'content_primary' : 'content_secondary'}
                >
                  {item.text}
                </Typography>
              </Flex.Row>
            );
          }}
        />
      )}

      {isFetching ? (
        <GoodsCarouselSkeleton count={4} />
      ) : (
        <UIKitGoodsCarousel
          likeMap={likeMap}
          listTestID={
            entity?.[0]?.logging.analytics?.LIST_ID as string | undefined
          }
          onPressLike={handlePressLike}
          size={'medium'}
          showRanking={entity.some(item => isNotNil(item.render?.data.ranking))}
          list={goodsData}
          onPressItem={async ({ item, logging }) => {
            handlePressItem({ logging, item });
          }}
          keyExtractor={(item, index) => (item.sno ?? index).toString()}
          onViewableItemsChanged={logDisplayGoods}
          viewabilityConfig={VIEWABILITY_CONFIG}
        />
      )}
    </Flex>
  );
};
